import { config } from '@/forms/config';
import { ValidationAdapter } from '@/forms/core';
import type {
  FormConditionalValidation,
  FormField,
  FormInitialValues,
  FormValidations,
  FormValidationSchema,
  LimitPair,
  LimitPairsRestriction,
} from '@/forms/typings';
import { formFieldHasFormikValue } from './formFieldHasFormikValue';
import { generateConditionalValidation } from './generateConditionalValidation';
import { generateConditionalValidationFieldIds } from './generateConditionalValidationFieldIds';

/** The synthetic Formik key carrying a limit amount's rule error. */
export const limitRuleKey = (amountFieldId: string): string =>
  `${amountFieldId}${config.limitRuleFieldSuffix}`;

/** Resolves a child by its seed ItemID. groupFieldId already carries the instance index. */
const findChildByItemID = (
  children: FormField[],
  groupFieldId: string,
  itemID: string,
): FormField | undefined =>
  children.find(child => child.fieldId === `${groupFieldId}_${itemID}`);

/** Numeric value of a currency-masked amount, or undefined when blank or unparseable. */
export const readLimitAmount = (raw: unknown): number | undefined => {
  if (typeof raw === 'number') return Number.isFinite(raw) ? raw : undefined;
  if (typeof raw !== 'string') return undefined;

  // The @currency mask stores bare digits; stripping tolerates values written before it.
  const cleaned = raw.replace(/[$,\s]/g, '');
  if (cleaned === '') return undefined;

  const parsed = Number(cleaned);
  return Number.isFinite(parsed) ? parsed : undefined;
};

/**
 * Yup schema for one amount's synthetic Formik key. Mirrors the server's order —
 * Unlimited, then presence, then minimum, then ordering — so the client can
 * never accept a form the server rejects, and picks the message that matches
 * whichever step failed.
 */
export const createLimitAmountYupSchema = (
  children: FormField[],
  pair: LimitPair,
  amountItemID: string,
  groupFieldId: string,
  conditions: FormConditionalValidation[] | undefined,
) => {
  const Yup = ValidationAdapter.getLibrary();
  const requiredMessage = pair.requiredMessage?.trim() || 'This field is required.';
  const comparisonMessage =
    pair.comparisonMessage?.trim() ||
    'The per claim limit must be equal to or less than the aggregate limit.';

  const base = Yup.mixed().nullable();

  const tested = base.test({
    name: 'limitPair',
    test() {
      const root = this.parent as Record<string, unknown> | undefined;
      if (!root) return true;

      if (pair.unlimitedItemID) {
        const toggle = findChildByItemID(children, groupFieldId, pair.unlimitedItemID);

        // An unresolvable toggle fails open, so the server can never reject a form
        // the client accepted.
        if (!toggle) return true;

        // An untouched checkbox has no Formik value; unticked reads as false.
        if (root[toggle.fieldId] === true) return true;
      }

      const amount = findChildByItemID(children, groupFieldId, amountItemID);

      // A misconfigured seed must never make the client stricter than the server.
      if (!amount) return true;

      // Server's IsEmpty() uses string.IsNullOrWhiteSpace: an untrimmed check here
      // would let a whitespace-only entry through to a BUS_002 on submit.
      const ownValue = root[amount.fieldId];
      const ownIsBlank =
        typeof ownValue === 'string'
          ? ownValue.trim() === ''
          : !formFieldHasFormikValue(amount, root);

      if (ownIsBlank) return this.createError({ message: requiredMessage });

      const ownAmount = readLimitAmount(ownValue);

      // A malformed entry is neither a floor failure nor an ordering failure.
      if (ownAmount === undefined) return true;

      const minimum =
        amountItemID === pair.lowerItemID ? pair.lowerMinimum : pair.upperMinimum;

      // Amount is a general FormField, but only the single-field variants carry readonly.
      const isReadonly =
        'readonly' in amount ? (amount as { readonly?: boolean }).readonly : false;

      // Mirrors LimitMinimumRule: the floor is checked before ordering because
      // "raise this amount" is the more actionable instruction. Skipped on a
      // readonly amount — a provider cannot raise a value they cannot edit.
      // Broader than the server's skip (IsEditable only): `readonly` also folds
      // in the definition's Readonly. Sound only while no limit amount seeds Readonly.
      if (minimum && !isReadonly && ownAmount < minimum.amount)
        return this.createError({ message: minimum.message });

      // Only the lower amount carries the ordering message — it is the one to lower.
      if (amountItemID !== pair.lowerItemID) return true;

      const upper = findChildByItemID(children, groupFieldId, pair.upperItemID);
      if (!upper) return true;

      const upperAmount = readLimitAmount(root[upper.fieldId]);

      // A malformed entry is not an ordering failure.
      if (upperAmount === undefined) return true;

      return ownAmount <= upperAmount
        ? true
        : this.createError({ message: comparisonMessage });
    },
  });

  if (!conditions?.length) return tested;

  return base.when(
    generateConditionalValidationFieldIds(conditions),
    (values, schema) =>
      generateConditionalValidation(conditions, values) ? tested : schema,
  );
};

/**
 * Registers one synthetic Formik key per amount of every pair (seeded undefined so
 * each is touched on submit) and its gated schema.
 */
export const registerLimitPairsValidation = (
  initialValues: FormInitialValues,
  validationFields: FormValidations,
  groupFieldId: string,
  children: FormField[],
  restriction: LimitPairsRestriction,
  conditions: FormConditionalValidation[] | undefined,
): void => {
  for (const pair of restriction.pairs)
    for (const amountItemID of [pair.lowerItemID, pair.upperItemID]) {
      const amount = findChildByItemID(children, groupFieldId, amountItemID);

      // An unresolvable amount has no field to render its message beside.
      if (!amount) continue;

      const key = limitRuleKey(amount.fieldId);
      initialValues[key] = undefined;
      validationFields[key] = createLimitAmountYupSchema(
        children,
        pair,
        amountItemID,
        groupFieldId,
        conditions,
      ) as FormValidationSchema;
    }
};
